import Logo from "@/components/Logo";
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Flame, Zap, Trophy, Check, Lock, BookOpen, NotebookPen, ArrowRight, Award, ScanEye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FeatureGate } from "@/components/FeatureGate";
import VerifyBanner from "@/components/VerifyBanner";
import { openBillingPortal } from "@/lib/billing";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatChip({ icon: Icon, label, value, color }) {
  return (
    <div className="glass-card px-4 py-3 flex items-center gap-3" data-testid={`stat-${label.toLowerCase()}`}>
      <Icon className="h-5 w-5" style={{ color }} strokeWidth={1.75} />
      <div>
        <div className="section-label text-[8px] text-zinc-500">{label}</div>
        <div className="font-heading text-lg font-light leading-none mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function LessonNode({ lesson, unlocked, index, guestLocked }) {
  const state = lesson.completed ? "done" : unlocked ? "open" : "locked";
  const inner = (
    <div
      className={`glass-card p-4 flex items-center gap-4 transition-colors ${state === "locked" && !guestLocked ? "opacity-45" : "hover:border-white/25"}`}
      data-testid={`lesson-node-${lesson.id}`}
    >
      <span className={`h-9 w-9 shrink-0 rounded-full grid place-items-center border ${
        state === "done" ? "bg-[#7ee0a1] text-[#0A0A0A] border-transparent"
        : state === "open" ? "border-[#9fd3ff] text-[#9fd3ff]" : "border-white/15 text-zinc-600"}`}>
        {state === "done" ? <Check className="h-4 w-4" /> : state === "locked" ? <Lock className="h-3.5 w-3.5" /> : index + 1}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 font-medium truncate">{lesson.title}</div>
        <div className="text-[12px] text-zinc-500 font-light truncate">{guestLocked ? "Subscribe to unlock this lesson" : lesson.summary}</div>
      </div>
      <span className={`section-label text-[8px] ${guestLocked ? "text-[#9fd3ff]" : "text-[#e6c67a]"}`}>{guestLocked ? "Unlock" : `+${lesson.xp} XP`}</span>
      {state !== "locked" && <ArrowRight className="h-4 w-4 text-zinc-600" />}
    </div>
  );
  if (guestLocked) return <Link to="/#pricing" data-testid={`lesson-upgrade-${lesson.id}`}>{inner}</Link>;
  if (state === "locked") return inner;
  return <Link to={`/learn/lesson/${lesson.id}`}>{inner}</Link>;
}

function TrainerDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/trainer/overview`, { credentials: "include" });
      if (r.ok) setData(await r.json());
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-20 text-center section-label text-[11px] text-zinc-600 animate-pulse">Loading your curriculum…</div>;

  const { progress, badges, seasons } = data;
  const earned = badges.filter((b) => b.earned);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="section-label text-[10px] text-[#9fd3ff]">MBM Trading Trainer</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-light tracking-tight">
            Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/mentor" className="btn-solid px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2" data-testid="open-mentor-btn">
            <ScanEye className="h-4 w-4" /> Chart Mentor
          </Link>
          <Link to="/journal" className="btn-glass text-white px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2" data-testid="open-journal-btn">
            <NotebookPen className="h-4 w-4" /> Trading Journal
          </Link>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <StatChip icon={Zap} label="XP" value={progress.xp} color="#e6c67a" />
        <StatChip icon={Flame} label="Streak" value={`${progress.streak} day${progress.streak === 1 ? "" : "s"}`} color="#ff9d5c" />
        <StatChip icon={Trophy} label="Stage" value={progress.stage} color="#9fd3ff" />
        <StatChip icon={BookOpen} label="Lessons" value={progress.completed_count} color="#7ee0a1" />
      </div>

      {earned.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="earned-badges">
          {earned.map((b) => (
            <span key={b.id} className="inline-flex items-center gap-1.5 text-[11px] text-[#e6c67a] border border-[#e6c67a]/30 rounded-full px-3 py-1.5">
              <Award className="h-3.5 w-3.5" /> {b.name}
            </span>
          ))}
        </div>
      )}

      {data.guest && (
        <Link to="/#pricing" className="mt-6 block glass-card glass-card-featured p-5 hover:border-white/25 transition-colors" data-testid="guest-upgrade-banner">
          <div className="flex items-center gap-3">
            <Award className="h-5 w-5 text-[#9fd3ff]" />
            <div className="flex-1">
              <div className="text-sm text-zinc-100 font-medium">You're on Guest access</div>
              <div className="text-[12px] text-zinc-400 font-light">Your first {data.guest_lesson_limit} Foundation lessons are free. Subscribe to unlock the full curriculum, unlimited AI Coach and journaling.</div>
            </div>
            <span className="btn-solid px-4 py-2 text-xs font-medium whitespace-nowrap">View plans</span>
          </div>
        </Link>
      )}

      <div className="mt-10 space-y-10">
        {seasons.map((s) => (
          <div key={s.id} data-testid={`season-${s.id}`}>
            <div className="flex items-center gap-3 mb-4">
              <span className="section-label text-[10px] text-zinc-300">{s.title}</span>
              <span className="text-[10px] text-zinc-600">· {s.level}</span>
            </div>
            <p className="text-sm text-zinc-500 font-light mb-5 max-w-2xl">{s.description}</p>
            <div className="space-y-2.5 max-w-2xl">
              {s.lessons.map((l, i) => (
                <LessonNode key={l.id} lesson={l} index={i}
                  unlocked={!l.locked && (i === 0 || s.lessons[i - 1].completed)} guestLocked={l.locked} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <button onClick={logout} className="mt-12 section-label text-[10px] text-zinc-500 hover:text-white transition-colors" data-testid="learn-logout">
        Log out
      </button>
    </>
  );
}

function RedeemCodeCard() {
  const { entitlements, refreshEntitlements } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  if (!entitlements || entitlements.is_staff || entitlements.membership !== "guest") return null;
  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/invites/redeem`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ code }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Could not redeem code");
      toast.success(`Welcome! ${d.plan} unlocked${d.forever ? " — never expires" : ""} 🎉`);
      setCode("");
      if (refreshEntitlements) await refreshEntitlements();
      window.location.reload();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div className="mb-8 glass-card p-4 flex flex-wrap items-center gap-3" data-testid="redeem-code-card">
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm text-zinc-100 font-medium">Have an access code?</div>
        <div className="text-[11px] text-zinc-500">Family &amp; friends codes unlock full membership instantly.</div>
      </div>
      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="MBM-XXXXXX"
        onKeyDown={(e) => e.key === "Enter" && redeem()}
        className="w-40 bg-white/[0.04] border border-white/12 rounded-lg px-3 py-2.5 text-sm text-zinc-200 font-mono tracking-widest focus:outline-none focus:border-[#9fd3ff]/40"
        data-testid="redeem-code-input" />
      <button onClick={redeem} disabled={busy || !code.trim()} className="btn-solid px-4 py-2.5 text-xs font-medium disabled:opacity-50" data-testid="redeem-code-btn">
        {busy ? "Checking…" : "Redeem"}
      </button>
    </div>
  );
}

export default function Learn() {
  const { entitlements } = useAuth();
  const paidMember = entitlements && !entitlements.is_staff && entitlements.membership !== "guest";
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA]">
      <div className="grain" />
      <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/8">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" aria-label="MBM home"><Logo className="h-5 w-auto" /></Link>
            <span className="text-zinc-700">/</span>
            <span className="section-label text-[10px] text-[#9fd3ff]">Trainer</span>
          </div>
          <div className="flex items-center gap-4 text-xs">
            {entitlements?.is_staff && (
              <Link to="/desk" className="text-[#e6c67a] hover:text-white transition-colors" data-testid="nav-coach-desk">Coach Desk</Link>
            )}
            <Link to="/mentor" className="text-zinc-400 hover:text-white transition-colors" data-testid="nav-chart-mentor">Chart Mentor</Link>
            <Link to="/community" className="text-zinc-400 hover:text-white transition-colors">Community</Link>
            <Link to="/intelligence" className="text-zinc-400 hover:text-white transition-colors">Intelligence</Link>
            {paidMember && (
              <button
                onClick={async () => { try { await openBillingPortal(); } catch (e) { toast.error(e.message); } }}
                className="text-zinc-400 hover:text-white transition-colors"
                data-testid="learn-manage-billing">
                Manage Billing
              </button>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-10" data-testid="learn-home">
        <VerifyBanner />
        <RedeemCodeCard />
        <FeatureGate feature="trading_trainer">
          <TrainerDashboard />
        </FeatureGate>
      </main>
    </div>
  );
}
